import type { FC } from 'react';
import { Row, Col, Container } from 'react-bootstrap';

interface HomePageProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
}

const HomePage: FC<HomePageProps> = ({ activeTab, setActiveTab }) => {

  return (
    <>
      {/* Banner */}
      <header className="banner-content">
        <div className="banner-title" style={{ marginTop: '3rem' }}>
          <h1
            className="banner-title-text"
            style={{ fontSize: '2.5rem', fontWeight: 700, marginBottom: '1rem' }}
          >
            AI Solutions Built on Security-First Principles
          </h1>
          <p
            className="banner-title-text"
            style={{ fontSize: '1.25rem', fontWeight: 300, marginBottom: '2rem' }}
          >
            Enterprise AI integration with security, governance, and reliability at the core
          </p>
          <a
            href="#contact"
            className="btn btn-lg"
            style={{
              backgroundColor: 'var(--secondary-color)',
              color: '#fff',
              fontWeight: 600,
              padding: '1rem 2.5rem',
            }}
            onClick={(e) => {
              e.preventDefault();
              setActiveTab('contact');
            }}
          >
            Schedule a Consultation
          </a>
        </div>
      </header>

      {/* Tab Content */}
      <div className="align-items-start text-center justify-content-center" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
        <div className="tab-content" id="nav-tabContent" style={{ width: '100%', maxWidth: '1400px', margin: '0 auto' }}>

          {/* Mission Tab */}
          <div
            className={`tab-pane fade ${activeTab === 'mission' ? 'show active' : ''}`}
            id="mission"
            role="tabpanel"
          >
              <Container style={{ marginLeft: 'auto', marginRight: 'auto' }}>
                <Row className="top-row">
                  <Col xs={12} className="bg-odd-header">
                    <h2 className="text-white">Our Mission</h2>
                  </Col>
                </Row>
                <Row>
                  <Col xs={12} className="bg-light">
                    <div className="company-text" style={{ textAlign: 'center', maxWidth: '900px', margin: '2rem auto' }}>
                      <p style={{ fontSize: '1.15rem', lineHeight: 1.8 }}>
                        Illuminative Solutions brings AI capabilities to enterprises with a security-first, responsible approach.
                        We combine deep expertise in machine learning, computer vision, and GenAI with proven security practices
                        to deliver AI solutions you can trust.
                      </p>
                      <p style={{ fontSize: '1.05rem', lineHeight: 1.7, marginTop: '1.5rem', color: 'var(--text-color-dark)' }}>
                        From strategy to implementation to ongoing optimization, we guide organizations through their AI journey
                        with technical rigor and business pragmatism.
                      </p>
                    </div>
                  </Col>
                </Row>
              </Container>
          </div>

          {/* Services Tab */}
          <div
            className={`tab-pane fade ${activeTab === 'services' ? 'show active' : ''}`}
            id="services"
            role="tabpanel"
          >
              <Container style={{ marginLeft: 'auto', marginRight: 'auto' }}>
                <Row className="top-row">
                  <Col xs={12} className="bg-odd-header">
                    <h2 className="text-white">Our Services</h2>
                  </Col>
                </Row>

                <Row style={{ marginTop: '2rem' }}>
                  {/* GenAI & LLM Integration */}
                  <Col xs={12} md={6} style={{ marginBottom: '2rem' }}>
                    <div className="card" style={{ height: '100%', textAlign: 'left' }}>
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
                        <i className="bi bi-chat-dots" style={{ fontSize: '2.5rem', color: 'var(--primary-color)', marginRight: '1rem' }}></i>
                        <h3 style={{ margin: 0, color: 'var(--primary-color)' }}>GenAI & LLM Integration</h3>
                      </div>
                      <p style={{ marginBottom: '1rem' }}>
                        Custom AI assistants, chatbots, and automation leveraging GPT-4, Claude, and other frontier models.
                      </p>
                      <ul style={{ margin: 0, paddingLeft: '1.5rem', color: 'var(--text-color-dark)' }}>
                        <li>Custom LLM application development</li>
                        <li>Prompt engineering & optimization</li>
                        <li>Multi-agent systems</li>
                        <li>API integration & workflow automation</li>
                      </ul>
                    </div>
                  </Col>

                  {/* RAG & Knowledge Systems */}
                  <Col xs={12} md={6} style={{ marginBottom: '2rem' }}>
                    <div className="card" style={{ height: '100%', textAlign: 'left' }}>
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
                        <i className="bi bi-database" style={{ fontSize: '2.5rem', color: 'var(--primary-color)', marginRight: '1rem' }}></i>
                        <h3 style={{ margin: 0, color: 'var(--primary-color)' }}>RAG & Knowledge Systems</h3>
                      </div>
                      <p style={{ marginBottom: '1rem' }}>
                        Build intelligent systems that reason over your proprietary data with retrieval-augmented generation.
                      </p>
                      <ul style={{ margin: 0, paddingLeft: '1.5rem', color: 'var(--text-color-dark)' }}>
                        <li>Document processing & embedding pipelines</li>
                        <li>Vector database design & optimization</li>
                        <li>Hybrid search implementations</li>
                        <li>Knowledge graph integration</li>
                      </ul>
                    </div>
                  </Col>

                  {/* Computer Vision & ML Models */}
                  <Col xs={12} md={6} style={{ marginBottom: '2rem' }}>
                    <div className="card" style={{ height: '100%', textAlign: 'left' }}>
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
                        <i className="bi bi-eye" style={{ fontSize: '2.5rem', color: 'var(--primary-color)', marginRight: '1rem' }}></i>
                        <h3 style={{ margin: 0, color: 'var(--primary-color)' }}>Computer Vision & ML Models</h3>
                      </div>
                      <p style={{ marginBottom: '1rem' }}>
                        Advanced vision systems and custom machine learning models tailored to your specific use cases.
                      </p>
                      <ul style={{ margin: 0, paddingLeft: '1.5rem', color: 'var(--text-color-dark)' }}>
                        <li>Object detection & classification</li>
                        <li>Image segmentation & analysis</li>
                        <li>Custom model training & fine-tuning</li>
                        <li>Edge deployment & optimization</li>
                      </ul>
                    </div>
                  </Col>

                  {/* AI Security & Governance */}
                  <Col xs={12} md={6} style={{ marginBottom: '2rem' }}>
                    <div className="card" style={{ height: '100%', textAlign: 'left' }}>
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
                        <i className="bi bi-shield-check" style={{ fontSize: '2.5rem', color: 'var(--primary-color)', marginRight: '1rem' }}></i>
                        <h3 style={{ margin: 0, color: 'var(--primary-color)' }}>AI Security & Governance</h3>
                      </div>
                      <p style={{ marginBottom: '1rem' }}>
                        Ensure your AI systems are secure, compliant, and aligned with responsible AI principles.
                      </p>
                      <ul style={{ margin: 0, paddingLeft: '1.5rem', color: 'var(--text-color-dark)' }}>
                        <li>Security audits & threat modeling</li>
                        <li>PII detection & data protection</li>
                        <li>Model governance frameworks</li>
                        <li>Compliance & regulatory alignment</li>
                      </ul>
                    </div>
                  </Col>
                </Row>
              </Container>
          </div>

          {/* About Tab */}
          <div
            className={`tab-pane fade ${activeTab === 'about' ? 'show active' : ''}`}
            id="about"
            role="tabpanel"
          >
              <Container style={{ marginLeft: 'auto', marginRight: 'auto' }}>
                <Row className="top-row">
                  <Col xs={12} className="bg-odd-header">
                    <h2 className="text-white">About Us</h2>
                  </Col>
                </Row>
                <Row>
                  <Col xs={12} className="bg-light">
                    <div className="company-text" style={{ textAlign: 'center', maxWidth: '900px', margin: '2rem auto' }}>
                      <p style={{ fontSize: '1.15rem', lineHeight: 1.8 }}>
                        Illuminative Solutions was founded by AI and security practitioners who saw the need for a different
                        approach to enterprise AI adoption - one that puts security, governance, and responsible AI at the
                        forefront rather than as an afterthought.
                      </p>
                      <p style={{ fontSize: '1.05rem', lineHeight: 1.7, marginTop: '1.5rem', color: 'var(--text-color-dark)' }}>
                        Our team brings decades of combined experience in machine learning, computer vision, cybersecurity,
                        and enterprise software engineering. We've worked across industries including healthcare, finance,
                        defense, and technology, giving us deep insight into the unique challenges each sector faces.
                      </p>
                      <p style={{ fontSize: '1.05rem', lineHeight: 1.7, marginTop: '1.5rem', color: 'var(--text-color-dark)' }}>
                        We believe that AI should augment human capabilities, not replace human judgment. Our solutions are
                        designed to be transparent, explainable, and aligned with your organization's values and requirements.
                      </p>
                    </div>
                  </Col>
                </Row>
              </Container>
          </div>

          {/* Contact Tab */}
          <div
            className={`tab-pane fade ${activeTab === 'contact' ? 'show active' : ''}`}
            id="contact"
            role="tabpanel"
          >
              <Container style={{ marginLeft: 'auto', marginRight: 'auto' }}>
                <Row className="top-row">
                  <Col xs={12} className="bg-odd-header">
                    <h2 className="text-white">Contact Us</h2>
                  </Col>
                </Row>
                <Row>
                  <Col xs={12} className="bg-light">
                    <div className="company-text" style={{ textAlign: 'center', maxWidth: '900px', margin: '2rem auto' }}>
                      <p style={{ fontSize: '1.15rem', lineHeight: 1.8, marginBottom: '2rem' }}>
                        Ready to discuss how AI can transform your organization? We'd love to hear from you.
                      </p>
                      <div style={{ textAlign: 'left', maxWidth: '600px', margin: '0 auto' }}>
                        <p style={{ fontSize: '1.1rem', marginBottom: '1rem' }}>
                          <strong>Email:</strong>{' '}
                          <a href="mailto:info@illsol.ai" style={{ color: 'var(--primary-color)' }}>
                            info@illsol.ai
                          </a>
                        </p>
                        <p style={{ fontSize: '1.1rem', marginBottom: '1rem' }}>
                          <strong>Phone:</strong> Available upon request
                        </p>
                        <p style={{ fontSize: '1.1rem', marginBottom: '2rem' }}>
                          <strong>Location:</strong> Washington, DC Metro Area
                        </p>
                        <p style={{ fontSize: '1rem', color: 'var(--text-color-dark)' }}>
                          We typically respond to inquiries within 24 hours. For urgent matters, please indicate that in
                          your email subject line.
                        </p>
                      </div>
                    </div>
                  </Col>
                </Row>
              </Container>
          </div>
        </div>
      </div>
    </>
  );
};

export default HomePage;
